import React from "react";
import { Eye, Heart, Play, Flame, Sparkles, Clock } from "lucide-react";
import { motion, AnimatePresence } from 'motion/react';
import { Video, Scraper, getProxiedThumbnailUrl } from "../types";

interface VideoGridProps {
  videos: Video[];
  onVideoSelect: (video: Video) => void;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  scrapers?: Scraper[];
}

export default function VideoGrid({
  videos,
  onVideoSelect,
  activeCategory,
  setActiveCategory,
  scrapers = [],
}: VideoGridProps) {
  // Helper to determine if a video qualifies as a Short (either Category is Shorts or duration <= 60s)
  const isShortVideo = (v: Video) => {
    if (v.category?.toLowerCase() === "shorts" || v.category?.toLowerCase() === "short") return true;
    if (!v.duration) return false;
    const parts = v.duration.split(":").map(Number);
    if (parts.length === 2) {
      const [m, s] = parts;
      return m === 0 || (m === 1 && s === 0);
    }
    if (parts.length === 1) {
      return parts[0] <= 60;
    }
    return false;
  };

  // Include all videos in the homepage video grid
  const allVideos = videos;

  // Extract unique categories dynamically from all videos and scrapers with case-insensitivity deduplication
  const categories = ["All", "Madison Flame"];

  // All category displays videos across ALL tags without exclusion in current session order
  const filteredVideos = activeCategory === "All"
    ? allVideos
    : allVideos.filter((v) => (v.category || "").toLowerCase() === activeCategory.toLowerCase());

  // Pagination / Display limit to prevent browser lag with 2000+ video DOM nodes
  const [displayCount, setDisplayCount] = React.useState(36);

  // Reset display count when category changes
  React.useEffect(() => {
    setDisplayCount(36);
  }, [activeCategory]);

  const visibleVideos = filteredVideos.slice(0, displayCount);

  // Helper to format large view/like counts
  const formatCount = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  return (
    <div id="video-grid-container" className="space-y-6">
      
      {/* Dynamic Categories Selection Capsule */}
      <div className="flex items-center gap-2 overflow-x-auto pb-3 scrollbar-none scroll-smooth">
        {categories.map((category) => {
          const isActive = activeCategory === category;
          return (
            <motion.button
              id={`cat-btn-${category.toLowerCase().replace(/\s+/g, '-')}`}
              key={category}
              onClick={() => setActiveCategory(category)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95, rotateX: 10, rotateY: 5 }}
              style={{ perspective: 1000 }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors duration-300 ${
                isActive
                  ? "bg-gradient-to-r from-green-500 to-emerald-600 text-zinc-950 shadow-md shadow-green-500/30 ring-2 ring-green-500/50"
                  : "bg-black/60 hover:bg-zinc-900 text-slate-300 border border-green-500/30 hover:border-green-500 hover:text-white backdrop-blur-md"
              }`}
            >
              {category === "Madison Flame" && <Flame className="w-3.5 h-3.5 text-rose-500 fill-rose-500 animate-pulse" />}
              {category === "StripTube" && <Play className="w-3.5 h-3.5 text-green-400 fill-green-400" />}
              {category === "Leaks" && <Sparkles className="w-3.5 h-3.5 text-amber-400" />}
              {category === "MMS" && <Flame className="w-3.5 h-3.5 text-red-500" />}
              {category === "ViralXXXPorn" && <Sparkles className="w-3.5 h-3.5 text-emerald-400" />}
              {category === "Russian" && <Flame className="w-3.5 h-3.5 text-cyan-400" />}
              {category === "Trending" && <Flame className="w-3.5 h-3.5 text-green-700" />}
              {category === "Hot" && <Flame className="w-3.5 h-3.5 text-green-500" />}
              {category === "Popular" && <Sparkles className="w-3.5 h-3.5 text-emerald-400" />}
              <span>{category}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Grid Layout */}
      {filteredVideos.length === 0 ? (
        <div className="text-center py-24 bg-slate-900/40 rounded-2xl border border-slate-800/60">
          <p className="text-slate-400 font-sans text-base">No videos found in this category.</p>
          <button
            onClick={() => setActiveCategory("All")}
            className="mt-4 text-xs font-bold text-rose-500 hover:underline"
          >
            Show All Videos
          </button>
        </div>
      ) : (
        <>
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2">
            <AnimatePresence mode="popLayout">
              {visibleVideos.map((video, index) => (
                <motion.div
                  layout
                  id={`video-card-${video.id}`}
                  key={video.id}
                  initial={{ opacity: 0, scale: 0.9, y: 15 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                  transition={{ duration: 0.3, delay: Math.min(index, 12) * 0.03, layout: { duration: 0.3 } }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => onVideoSelect(video)}
                  className="group cursor-pointer bg-black/40 backdrop-blur-sm rounded-xl overflow-hidden border border-green-500/20 hover:border-green-400 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all duration-300 flex flex-col justify-between"
                >
                {/* Thumbnail Stage */}
                <div className="relative aspect-video w-full overflow-hidden bg-zinc-950">
                  <img
                    src={getProxiedThumbnailUrl(video.thumbnailUrl, video.title, video.streamUrl || video.videoUrl)}
                    alt={video.title}
                    onError={(e) => {
                      const img = e.currentTarget;
                      const attempt = parseInt(img.getAttribute("data-attempt") || "0", 10);
                      if (attempt === 0) {
                        img.setAttribute("data-attempt", "1");
                        const pageUrl = video.streamUrl || video.videoUrl;
                        if (pageUrl) {
                          img.src = `/api/smart-thumb?pageUrl=${encodeURIComponent(pageUrl)}`;
                          return;
                        }
                      }
                      img.setAttribute("data-attempt", "2");
                      img.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360" fill="%230f172a"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="%230f172a"/><stop offset="100%" stop-color="%23020617"/></linearGradient></defs><rect width="100%" height="100%" fill="url(%23g)"/><circle cx="320" cy="150" r="38" fill="%2322c55e" fill-opacity="0.25"/><polygon points="312,135 336,150 312,165" fill="%2322c55e"/><text x="50%" y="78%" fill="%23cbd5e1" font-family="sans-serif" font-weight="bold" font-size="15" text-anchor="middle">STREAM READY</text><text x="50%" y="88%" fill="%2322c55e" font-family="monospace" font-weight="bold" font-size="11" text-anchor="middle">⚡ ULTRA HD</text></svg>`;
                    }}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Visual Glass Overlays */}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-green-500/90 text-white flex items-center justify-center scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300 shadow-[0_0_15px_#22c55e]">
                      <Play className="w-5 h-5 fill-white ml-0.5 drop-shadow-md" />
                    </div>
                  </div>

                  {/* Duration Tag */}
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/80 text-[10px] font-bold text-green-400 tracking-wider flex items-center gap-1 border border-green-500/30">
                    <Clock className="w-2.5 h-2.5" />
                    {video.duration}
                  </div>
                </div>

                {/* Detail Content Section */}
                <div className="p-3">
                  <h3 className="text-green-50 font-semibold text-sm group-hover:text-green-400 line-clamp-2 transition-colors leading-tight drop-shadow-sm">
                    {video.title}
                  </h3>
                </div>

              </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Load More Pagination Control */}
          {displayCount < filteredVideos.length && (
            <div className="flex flex-col items-center justify-center pt-8 pb-4 space-y-2">
              <motion.button
                id="load-more-btn"
                onClick={() => setDisplayCount((prev) => prev + 36)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95, rotateX: 20, rotateY: 10 }}
                style={{ perspective: 1000 }}
                className="px-8 py-3 rounded-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-black font-bold text-sm shadow-[0_0_15px_rgba(34,197,94,0.4)] hover:shadow-[0_0_25px_rgba(34,197,94,0.6)] transition-all duration-300 active:scale-95 flex items-center gap-2"
              >
                <span>Load More Videos</span>
                <span className="text-xs px-2 py-0.5 bg-black/30 rounded-full font-mono text-green-100">
                  {visibleVideos.length} / {filteredVideos.length}
                </span>
              </motion.button>
              <p className="text-[11px] text-green-400/60 font-mono">
                Showing {visibleVideos.length} of {filteredVideos.length} total videos
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
